import {
  Assessment as AssessmentIcon,
  Dashboard as DashboardIcon,
  FactCheck as FactCheckIcon,
  GridView as GridViewIcon,
  People as PeopleIcon,
  PeopleAlt as PeopleAltIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Container,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { format, subDays } from "date-fns";
import React, { useMemo, useState } from "react";
import {
  SalesManagerFilterParams,
  SalespersonActivityStats,
  useGetSalespersonActivityStatsQuery,
  useGetSalesOverviewQuery,
  useGetSalesTeamSummaryQuery,
} from "../../store/services/salesManagerService";
import KpiRow from "./KpiRow";
import SalesManagerFilters from "./SalesManagerFilters";
import ActiveSalespeopleDrawer from "./salespeople/ActiveSalespeopleDrawer";
import SalespersonDetailDrawer from "./salespeople/SalespersonDetailDrawer";
import { useActiveSalespeople } from "./salespeople/useActiveSalespeople";
import ActivityTrendsTab from "./tabs/ActivityTrendsTab";
import ExecutionByPersonTab from "./tabs/ExecutionByPersonTab";
import OverviewTab from "./tabs/OverviewTab";
import SalespeopleTab from "./tabs/SalespeopleTab";
import SegmentMatrixTab from "./tabs/SegmentMatrixTab";
import { useNavigate } from "react-router-dom";

type TabKey =
  | "overview"
  | "salespeople"
  | "execution"
  | "matrix"
  | "activities";

const TAB_CONFIG: {
  key: TabKey;
  label: string;
  icon: React.ReactElement;
}[] = [
  { key: "overview", label: "Översikt", icon: <DashboardIcon /> },
  { key: "salespeople", label: "Säljare", icon: <PeopleIcon /> },
  { key: "execution", label: "Utförande", icon: <FactCheckIcon /> },
  { key: "matrix", label: "Segment-matris", icon: <GridViewIcon /> },
  { key: "activities", label: "Aktiviteter", icon: <TimelineIcon /> },
];

// Datumet serialiseras i lokal tid (yyyy-MM-dd) — backend tolkar
// fromDate/toDate som hela dagar utan tidskomponent, så vi får aldrig
// lita på toISOString() som konverterar till UTC först och kan tappa en dag.
const initialFilters: SalesManagerFilterParams = {
  fromDate: format(subDays(new Date(), 30), "yyyy-MM-dd"),
  toDate: format(new Date(), "yyyy-MM-dd"),
};

const SalesManagerDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [filters, setFilters] =
    useState<SalesManagerFilterParams>(initialFilters);
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Säljar-detaljdrawer delas mellan Säljare- och Utförande-fliken så vi
  // lyfter upp state hit istället för att varje flik äger sin egen drawer.
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedSalesperson, setSelectedSalesperson] =
    useState<SalespersonActivityStats | null>(null);

  const handleSalespersonClick = (stats: SalespersonActivityStats) => {
    setSelectedSalesperson(stats);
    setDetailDrawerOpen(true);
  };
  const handleDetailDrawerClose = () => {
    setDetailDrawerOpen(false);
    // selectedSalesperson ligger kvar tills animationen är klar
  };
  const handleOpenFullProfile = (salespersonId: number) => {
    navigate(`/users/${salespersonId}/activity`);
  };

  // Persistent per-företag-lista av aktiva säljare. Styr vilka säljare
  // som hämtas från backend.
  const { activeUserNos, setActiveUserNos, toggleUserNo, clear } =
    useActiveSalespeople();

  // Slå ihop filtret med den persistenta säljarlistan innan vi skickar
  // till backend. Tom lista = visa alla (ingen userNos-param).
  const effectiveFilters = useMemo<SalesManagerFilterParams>(
    () => ({
      ...filters,
      userNos: activeUserNos.length > 0 ? activeUserNos : undefined,
    }),
    [filters, activeUserNos]
  );

  // refetchOnMountOrArgChange: 30 → om datan är färskare än 30 sek används
  // cachen rakt av vid remount/argbyte. Uppdatera-knappen kallar refetch()
  // explicit och kringgår cachen när användaren vill tvinga fram ny data.
  const CACHE_STALE_SECONDS = 30;

  const {
    data: salespersonStats,
    isFetching: isLoadingStats,
    error: statsError,
    refetch: refetchStats,
  } = useGetSalespersonActivityStatsQuery(effectiveFilters, {
    refetchOnMountOrArgChange: CACHE_STALE_SECONDS,
  });

  const {
    data: teamSummary,
    isFetching: isLoadingTeams,
    error: teamsError,
    refetch: refetchTeams,
  } = useGetSalesTeamSummaryQuery(effectiveFilters, {
    refetchOnMountOrArgChange: CACHE_STALE_SECONDS,
  });

  const {
    data: overview,
    isFetching: isLoadingOverview,
    error: overviewError,
    refetch: refetchOverview,
  } = useGetSalesOverviewQuery(effectiveFilters, {
    refetchOnMountOrArgChange: CACHE_STALE_SECONDS,
  });

  const handleRefreshAll = () => {
    refetchStats();
    refetchTeams();
    refetchOverview();
  };

  const isLoading = isLoadingStats || isLoadingTeams || isLoadingOverview;
  const hasError = statsError || teamsError || overviewError;

  if (hasError) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          Ett fel uppstod vid hämtning av data. Försök igen senare.
        </Alert>
        <Button onClick={handleRefreshAll} startIcon={<RefreshIcon />}>
          Försök igen
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4, position: "relative" }}>
      {/* Loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: alpha(theme.palette.background.default, 0.6),
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            borderRadius: 3,
          }}
        >
          <CircularProgress size={56} color="primary" thickness={4} />
        </Box>
      )}

      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              width: 56,
              height: 56,
            }}
          >
            <AssessmentIcon fontSize="large" />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Säljchef Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Uppföljning av aktiviteter per säljare, segment och aktivitetstyp
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <Badge
            badgeContent={activeUserNos.length}
            color="primary"
            overlap="rectangular"
            invisible={activeUserNos.length === 0}
          >
            <Button
              onClick={() => setDrawerOpen(true)}
              startIcon={<PeopleAltIcon />}
              variant="contained"
            >
              Välj säljare
            </Button>
          </Badge>
          <Button
            onClick={handleRefreshAll}
            startIcon={<RefreshIcon />}
            variant="outlined"
            disabled={isLoading}
          >
            Uppdatera
          </Button>
        </Stack>
      </Stack>

      {/* Filter */}
      <Box sx={{ mb: 3 }}>
        <SalesManagerFilters
          filters={filters}
          onFiltersChange={setFilters}
        />
      </Box>

      {/* KPI-rad */}
      <KpiRow overview={overview} isLoading={isLoading} />

      {/* Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 3,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {TAB_CONFIG.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.label}
              icon={tab.icon}
              iconPosition="start"
              sx={{ textTransform: "none", fontWeight: 600, minHeight: 56 }}
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab-innehåll */}
      <Box>
        {activeTab === "overview" && (
          <OverviewTab
            overview={overview}
            teamSummary={teamSummary}
            isLoading={isLoading}
          />
        )}
        {activeTab === "salespeople" && (
          <SalespeopleTab
            salespersonStats={salespersonStats}
            isLoading={isLoading}
            onRowClick={handleSalespersonClick}
          />
        )}
        {activeTab === "execution" && (
          <ExecutionByPersonTab
            salespersonStats={salespersonStats}
            isLoading={isLoading}
            onRowClick={handleSalespersonClick}
          />
        )}
        {activeTab === "matrix" && (
          <SegmentMatrixTab
            overview={overview}
            teamSummary={teamSummary}
            isLoading={isLoading}
          />
        )}
        {activeTab === "activities" && (
          <ActivityTrendsTab
            overview={overview}
            teamSummary={teamSummary}
            isLoading={isLoading}
          />
        )}
      </Box>

      <ActiveSalespeopleDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeUserNos={activeUserNos}
        onToggle={toggleUserNo}
        onSetAll={setActiveUserNos}
        onClear={clear}
      />

      <SalespersonDetailDrawer
        open={detailDrawerOpen}
        stats={selectedSalesperson}
        onClose={handleDetailDrawerClose}
        onOpenFullProfile={handleOpenFullProfile}
      />
    </Container>
  );
};

export default SalesManagerDashboard;
